import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { isFloatingPointNumber } from '@/utils/categUtils';
import MinMaxFilter from '@/components/categ/filters/MinMaxFilter';

import styles from './SliderFilter.module.scss';
import { PRICE_FILTER_DEFAULT } from '@/constants/categ';

const formatPriceId = (price) => isFloatingPointNumber(price) ? `price-${price}` : `price-${price.toFixed(1)}`;

const SliderFilter = ({
  min = PRICE_FILTER_DEFAULT.min,
  sliderDefaultCursorMin = PRICE_FILTER_DEFAULT.min,
  max = PRICE_FILTER_DEFAULT.max,
  sliderDefaultCursorMax = PRICE_FILTER_DEFAULT.max,
  step = PRICE_FILTER_DEFAULT.step,
  formatValue = (val) => val,
  onChange
}) => {
  const [minValue, setMinValue] = useState('');
  const [minPlaceholder, setMinPlaceholder] = useState(sliderDefaultCursorMin);
  const [maxValue, setMaxValue] = useState('');
  const [maxPlaceholder, setMaxPlaceholder] = useState(sliderDefaultCursorMax);
  const [maxSlider, setMaxSlider] = useState(max);
  const [minPlaceholderCustom, setMinPlaceholderCustom] = useState(min);
  const [maxPlaceholderCustom, setMaxPlaceholderCustom] = useState(max);

  // Can't use useMemo here : https://github.com/facebook/react/issues/16416
  useEffect(() => {
    setMinPlaceholderCustom(formatValue(minPlaceholder, false));
  }, [minPlaceholder]);

  // Can't use useMemo here : https://github.com/facebook/react/issues/16416
  useEffect(() => {
    setMaxPlaceholderCustom(formatValue(maxPlaceholder));
  }, [maxPlaceholder]);

  useEffect(() => {
    setMinPlaceholder(sliderDefaultCursorMin);
  }, [sliderDefaultCursorMin]);

  useEffect(() => {
    setMaxPlaceholder(Math.min(sliderDefaultCursorMax, PRICE_FILTER_DEFAULT.max));
  }, [sliderDefaultCursorMax]);

  useEffect(() => {
    let _max = Math.min(max, PRICE_FILTER_DEFAULT.max);

    // Override PRICE_FILTER_DEFAULT.max if min is Higher
    if (_max < min) {
      _max = max;
    }

    setMaxSlider(_max);
  }, [max, min]);

  useEffect(() => {
    // Override PRICE_FILTER_DEFAULT.max if min is Higher
    if (maxPlaceholder < min) {
      setMaxPlaceholder(max);
    }
  }, [max, min, maxPlaceholder]);

  const setPlaceholderAndOnChange = (value, triggerOnChange = true, isMin = true) => {
    const onChangeValue = {
      minValue: minPlaceholder,
      maxValue: maxPlaceholder
    };

    if (isMin) {
      setMinValue('');
      setMinPlaceholder(value);
      onChangeValue.minValue = value;
    } else {
      setMaxValue('');
      setMaxPlaceholder(value);
      onChangeValue.maxValue = value;
    }

    if (triggerOnChange) onChange(onChangeValue);
  };

  const handleBlurValue = (val, triggerOnChange = true, isMin = true) => {
    if (val || val === 0) {
      val = parseInt(val);

      if (isMin && (val > max || val > maxPlaceholder || val < min)) {
        val = min;
      } else if (!isMin && (val > max || val < minPlaceholder || val < min)) {
        val = max;
      }

      setPlaceholderAndOnChange(val, triggerOnChange, isMin);
    }
  };

  const handleRangeOnChange = (values, triggerOnChange = true) => {
    if (values?.length === 2) {
      // We must "triggerOnChange" just one time.
      // When Slider's values change, there is only one changement
      handleBlurValue(values[0], false);
      handleBlurValue(values[1], triggerOnChange, false);
    }
  };

  return (
    <div className={styles.container} data-test-id="slider-filter">
      <div className={styles['input-container']}>
        <input
          type="number"
          className="form-control"
          min={min}
          max={max}
          value={minValue || ''}
          placeholder={minPlaceholderCustom}
          onBlur={(e) => handleBlurValue(e.target.value)}
          onChange={(e) => setMinValue(e.target.value)}
          data-test-id="input-min"
          id={formatPriceId(min)}
        />
        <input
          type="number"
          className="form-control"
          min={min}
          max={max}
          value={maxValue || ''}
          placeholder={maxPlaceholderCustom}
          onBlur={(e) => handleBlurValue(e.target.value, true, false)}
          onChange={(e) => setMaxValue(e.target.value)}
          data-test-id="input-max"
          id={formatPriceId(max)}
        />
      </div>

      <MinMaxFilter
        values={[minPlaceholder, maxPlaceholder]}
        step={step}
        min={min}
        max={maxSlider}
        handleRangeOnChange={handleRangeOnChange}
      />
    </div>
  );
};

SliderFilter.propTypes = {
  min: PropTypes.number,
  sliderDefaultCursorMin: PropTypes.number,
  max: PropTypes.number,
  sliderDefaultCursorMax: PropTypes.number,
  step: PropTypes.number,
  formatValue: PropTypes.func,
  onChange: PropTypes.func.isRequired
};

export default SliderFilter;
